import { connect } from 'react-redux';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Field, Input, Label, MessageBar, MessageBarBody, MessageBarTitle, Spinner, Text, Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle, DrawerFooter, ProgressBar, Badge } from '@fluentui/react-components';
import { CheckmarkCircle24Filled, ArrowSyncDismiss24Filled, SyncOff16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import * as ComponentFunctions from './Contract.IAImport.Functions';
import { ContractIAImportProcess } from '../../../models/Entities/Contracts/ContractIAImportProcess';
import FileSelectorComponent from '../../../commons/component/Inputs/FileSelector/FileSelector.Component';
import { Separator } from '@fluentui/react';

// New enum mapping (ContractProcessStatusEnum):
// registered = 1, inProgress = 2, finished = 3, cancelled = 4, fail = 5
// We show a spinner while in registered or inProgress states.
const StatusIcon = ({ status }: { status?: number }) => {
  switch (status) {
    case 1: // registered
    case 2: // inProgress
      return <Spinner size="extra-tiny" />;
    case 3: // finished
      return <CheckmarkCircle24Filled color='lightgreen' />;
    case 4: // cancelled
      return <SyncOff16Regular color='#999' />;
    case 5: // fail
      return <ArrowSyncDismiss24Filled color='#cd3740' />;
    default:
      return <SyncOff16Regular />;
  }
};

const StatusText = ({ status, lastProcess }: { status?: number; lastProcess?: ContractIAImportProcess | null }) => {
  const { t } = useTranslation();
  let key: string;
  switch (status) {
    case 1:
      key = 'contractIAImport.status.registeredStarting';
      break;
    case 2:
      key = 'contractIAImport.status.inProgressImporting';
      break;
    case 3:
      key = 'contractIAImport.status.finished';
      break;
    case 4:
      key = 'contractIAImport.status.cancelled';
      break;
    case 5:
      key = 'contractIAImport.status.fail';
      break;
    default:
      key = 'contractIAImport.status.idle';
  }
  const lastImportRaw: any = lastProcess && (lastProcess.endTimestamp || lastProcess.dateTimeSync || lastProcess.startTimestamp);
  const lastImportDate = lastImportRaw ? new Date(lastImportRaw).toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }) : null;
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Text>{t(key)}</Text>
      {lastImportDate && (
        <Text size={200} style={{ color: '#666' }}>
          {t('contractIAImport.status.lastImportPrefix')} {lastImportDate}
        </Text>
      )}
    </div>
  );
};

const ContractIAImportComponent = (props: ComponentFunctions.Props) => {
  const { contractId, lastProcess, contract } = props;
  const { t } = useTranslation();

  const [fileIds, setFileIds] = useState<string[]>([]);
  const [hasPassword, setHasPassword] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [reWriteExistingData, setReWriteExistingData] = useState<boolean>(false);
  const [comments, setComments] = useState<string>('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const lastProcessIdRef = useRef<string | undefined>(undefined);

  // Verificar si el contrato tiene stages o concepts en algún addendum
  const hasStagesOrConcepts = useMemo(() => {
    if (!contract?.addendums || contract.addendums.length === 0) return false;
    return contract.addendums.some(addendum => 
      (addendum.stages && addendum.stages.length > 0) || 
      (addendum.concepts && addendum.concepts.length > 0)
    );
  }, [contract?.addendums]);

  // When IA import finishes, refresh stages and concepts by addendum
  ComponentFunctions.useIAImportCompletionRefresh(
    contractId,
    lastProcess,
    props.GetAllContractStagesByAddendumId,
    props.GetAllContractsConceptByAddendum,
    props.GetContractAddendumsByContractId
  );

  // Fetch last process on mount
  useEffect(() => {
    if (!props.isGettingLast && lastProcess == null && contractId) {
      props.GetLastImportContractProcess(contractId);
    }
  }, [contractId]);

  // Poll every 5s while status is registered (1) or inProgress (2)
  useEffect(() => {
    const isActiveStatus = lastProcess && (lastProcess.status === 1 || lastProcess.status === 2);
    if (!isActiveStatus) return;
    
    const interval = setInterval(() => {
      console.log('Polling for IA import process update...', { processId: lastProcess.id, status: lastProcess.status });
      props.GetLastImportContractProcess(contractId);
    }, 5000);
    return () => clearInterval(interval);
  }, [lastProcess?.status, contractId]);

  // Fetch process once after successful creation
  useEffect(() => {
    if (props.successCreating) {
      props.GetLastImportContractProcess(contractId);
    }
  }, [props.successCreating]);

  // Timer: count elapsed time since startTimestamp while status is 1 or 2
  useEffect(() => {
    const active = !!lastProcess && (lastProcess.status === 1 || lastProcess.status === 2);
    if (!active) {
      setElapsedTime(0);
      lastProcessIdRef.current = undefined;
      return;
    }

    if (lastProcess?.id && lastProcessIdRef.current !== lastProcess.id) {
      lastProcessIdRef.current = lastProcess.id;
    }

    // Calcular tiempo transcurrido desde el inicio ajustando a zona horaria del navegador
    const calculateElapsed = () => {
      if (lastProcess.startTimestamp) {
        // El timestamp viene en formato UTC, parsearlo correctamente
        // Limpiar el timestamp si tiene más de 3 dígitos en los milisegundos
        let cleanTimestamp = lastProcess.startTimestamp;
        if (typeof cleanTimestamp === 'string') {
          // Manejar formato con microsegundos (más de 3 dígitos después del punto)
          cleanTimestamp = cleanTimestamp.replace(/(\.\d{3})\d+/, '$1');
          
          // Si no tiene 'Z' al final, agregarlo para indicar que es UTC
          if (!cleanTimestamp.endsWith('Z')) {
            cleanTimestamp += 'Z';
          }
        }
        
        const startDate = new Date(cleanTimestamp);
        const startTime = startDate.getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000); // seconds
        
        console.log('Calculating elapsed time:', { 
          originalTimestamp: lastProcess.startTimestamp,
          cleanTimestamp,
          startDate: startDate.toISOString(),
          startDateLocal: startDate.toLocaleString(),
          startTime, 
          now, 
          difference: now - startTime,
          elapsed 
        });
        
        return elapsed > 0 ? elapsed : 0;
      }
      return 0;
    };

    // Actualizar inmediatamente
    setElapsedTime(calculateElapsed());
    
    // Actualizar cada segundo
    const timer = setInterval(() => {
      setElapsedTime(calculateElapsed());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [lastProcess?.id, lastProcess?.status, lastProcess?.startTimestamp]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (durationString?: string): string => {
    if (!durationString) return '0:00';
    
    // Parse duration string (format: "HH:mm:ss" or similar)
    const parts = durationString.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0] || '0', 10);
      const minutes = parseInt(parts[1] || '0', 10);
      const seconds = parseInt(parts[2] || '0', 10);
      const totalSeconds = hours * 3600 + minutes * 60 + seconds;
      return formatTime(totalSeconds);
    }
    
    // Fallback: try to parse as total seconds
    const totalSeconds = parseInt(durationString, 10);
    if (!isNaN(totalSeconds)) {
      return formatTime(totalSeconds);
    }
    
    return '0:00';
  };

  const canStart = useMemo(() => !!contractId && fileIds.length > 0 && !props.isCreating, [contractId, fileIds, props.isCreating]);

  const onOpenForm = () => setOpenDrawer(true);
  const onCloseForm = () => setOpenDrawer(false);
  const onStartImport = () => {
    props.CreateImportContractProcess({
      contractID: contractId,
      fileIds: fileIds,
      hasPassword: !!hasPassword,
      reWriteExistingData: !!reWriteExistingData,
      password: hasPassword ? password : undefined,
      additionalPromptInformation: comments?.trim() ? comments.trim() : undefined,
    });
    setOpenDrawer(false);
  };

  const onLocalFilesChange = (files: any[]) => {
    // Extraer todos los fileIds de los archivos seleccionados
    const ids = files?.map(file => String(file.fileId || file.id)).filter(id => id) || [];
    setFileIds(ids);
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {props.lastProcess === undefined && props.isGettingLast ? (
          <Spinner size="extra-tiny" />
        ) : (
          <StatusIcon status={lastProcess?.status} />
        )}
        {props.lastProcess === undefined && props.isGettingLast ? (
          <Text>{t('contractIAImport.status.checking')}</Text>
        ) : (
          <StatusText status={lastProcess?.status} lastProcess={lastProcess} />
        )}
      </div>
      {lastProcess && (lastProcess.status === 1 || lastProcess.status === 2) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Label>Tiempo transcurrido: {formatTime(elapsedTime)}</Label>
        </div>
      )}
      {lastProcess && (lastProcess.status === 3 || lastProcess.status === 4 || lastProcess.status === 5) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Label>Duración: {lastProcess.duration ? formatDuration(lastProcess.duration) : 'N/A'}</Label>
          {console.log('Duration display:', { status: lastProcess.status, duration: lastProcess.duration, formatted: lastProcess.duration ? formatDuration(lastProcess.duration) : 'N/A' })}
        </div>
      )}

      {!(lastProcess && (lastProcess.status === 1 || lastProcess.status === 2)) && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button appearance='primary' onClick={onOpenForm} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {props.isCreating ? t('common.saving') : t('contractIAImport.actions.openForm')}
            <Badge appearance="filled" color="informative" size="small">BETA</Badge>
          </Button>
          {/* <Button appearance='secondary' onClick={() => props.GetLastImportContractProcess(contractId)} disabled={props.isGettingLast}>
            {props.isGettingLast ? t('common.saving') : t('contractIAImport.actions.refresh')}
          </Button> */}
        </div>
      )}

      <Drawer position='end' open={openDrawer} onOpenChange={(_, data) => setOpenDrawer(data.open)}>
        <DrawerHeader>
          <DrawerHeaderTitle>{t('contractIAImport.form.title')}</DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <form autoComplete="off">
          <div style={{ display: 'grid', gap: 12 }}>
            <Field label={t('contractIAImport.form.fileId.label')} hint={`${t('contractIAImport.form.fileId.hint')}${fileIds.length > 0 ? ` (${fileIds.length} ${fileIds.length === 1 ? 'archivo seleccionado' : 'archivos seleccionados'})` : ''}`}>
              <FileSelectorComponent contractId={contractId} multiple={true} onChange={onLocalFilesChange} />
            </Field>
            <Separator />
            <Field>
              <Checkbox checked={hasPassword} onChange={(_, d) => setHasPassword(!!d.checked)} label={t('contractIAImport.form.passwordProtected')} />
            </Field>
            {hasPassword && (
              <Field label={t('contractIAImport.form.password')}>
                <Input
                  type="password"
                  value={password}
                  onChange={(_, d) => setPassword(d.value)}
                  name="contract-file-password"
                  autoComplete="new-password"
                />
              </Field>
            )}
            <Field>
              <Checkbox 
                checked={reWriteExistingData} 
                onChange={(_, d) => setReWriteExistingData(!!d.checked)} 
                label={t('contractIAImport.form.reWriteExistingData')} 
              />
            </Field>
            <Separator />
            <Field label={t('contractIAImport.form.additionalPromptInformation.label')} hint={t('contractIAImport.form.additionalPromptInformation.hint')}>
                <Input
                  type="text"
                  value={comments}
                  onChange={(_, d) => setComments(d.value)}
                  name="contract-file-comments"
                  autoComplete="off"
                />
              </Field>
          </div>
          </form>
        </DrawerBody>
        <DrawerFooter>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button appearance='secondary' onClick={onCloseForm}>{t('contractIAImport.actions.cancel')}</Button>
            <Button appearance='primary' disabled={!canStart} onClick={onStartImport}>
              {props.isCreating ? t('common.saving') : t('contractIAImport.actions.start')}
            </Button>
          </div>
        </DrawerFooter>
      </Drawer>

      {(props.failureCreating || props.failureGettingLast) && (
        <MessageBar intent='error'>
          <MessageBarBody>
            <MessageBarTitle>{t('contractIAImport.error.title')}</MessageBarTitle>
            {props.errorCreating?.ErrorMessage || props.errorGettingLast?.ErrorMessage}
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};

export default connect(ComponentFunctions.mapStateToProps, ComponentFunctions.mapDispatchToProps)(ContractIAImportComponent as any);
